'use client';

import { ComponentProps, CSSProperties, SyntheticEvent, useState } from 'react';
import Image, { StaticImageData } from 'next/image';
import { cn } from '@/shared/utils/style/cn';

const loadedSrc = new Map<ComponentProps<typeof Image>['src'], true>();
const MAX_CACHE_SIZE = 1000;
const DEFAULT_IMAGE = '/images/default-image.png';

function markSrcLoaded(src: ComponentProps<typeof Image>['src']) {
  if (loadedSrc.has(src)) return;
  if (loadedSrc.size >= MAX_CACHE_SIZE) {
    const first = loadedSrc.keys().next().value;
    if (first) {
      loadedSrc.delete(first);
    }
  }
  loadedSrc.set(src, true);
}

// 유효한 이미지 src 검사
function isValidImageSrc(src: unknown): src is string | StaticImageData {
  if (typeof src === 'string') {
    return src.includes('/');
  }
  return typeof src === 'object';
}

export function Img(
  props: Omit<ComponentProps<typeof Image>, 'objectFit' | 'style'> & {
    responsive?: boolean;
  },
) {
  const { responsive, onLoad, onError, src, sizes, className, ...rest } = props;

  const initialSrc = isValidImageSrc(src) ? src : DEFAULT_IMAGE;

  const [imgSrc, setImgSrc] = useState(initialSrc);
  const [loaded, setLoaded] = useState(() => loadedSrc.has(src));

  const loadingStyle: CSSProperties = {
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: loaded ? 1 : 0,
  };

  const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    if (!loaded) {
      markSrcLoaded(src);
      setLoaded(true);
    }
    onLoad?.(e);
  };

  const handleError = (e: SyntheticEvent<HTMLImageElement>) => {
    if (imgSrc !== DEFAULT_IMAGE) {
      setImgSrc(DEFAULT_IMAGE);
    }
    onError?.(e);
  };

  if (responsive) {
    return (
      <div className={cn('relative object-cover', className)}>
        <Image
          {...rest}
          src={imgSrc}
          fill
          sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
          style={{
            borderRadius: 'inherit',
            objectFit: 'inherit',
            ...loadingStyle,
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }
  return (
    <Image
      {...rest}
      src={imgSrc}
      sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
      className={cn('object-cover', className)}
      style={{
        ...loadingStyle,
      }}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
}
